/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { FiArrowRight, FiCalendar, FiGlobe } from 'react-icons/fi';
import add from 'date-fns/add';
import AppSelect from '../../../../shared/components/app-select/AppSelect';
import AppDatePicker from '../../../../shared/components/app-date-picker/AppDatePicker';
import {
  getNewDate,
  getAPIFormattedDate,
} from '../../../../shared/helper-utils/appUtils';
import { TODAY_DATE } from '../../../../shared/constants/Constants';
import useEsimCountries from '../../hooks/useEsimCountries';
import { ESIM_ROUTES, ESIM_DATE_LIMITS } from '../../constants/esimConstants';
import {
  buildEsimSearchQuery,
  tripDurationInDays,
} from '../../utils/esimUtils';
import './EsimSearchForm.scss';

function EsimSearchForm({ initialValues, onSearch, compact }) {
  const navigate = useNavigate();
  const { options, loading: countriesLoading } = useEsimCountries();

  const [form, setForm] = useState({
    country: '',
    travelStart: '',
    travelEnd: '',
    nationality: 'IN',
    ...initialValues,
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialValues) {
      setForm((prev) => ({ ...prev, ...initialValues }));
    }
  }, [initialValues]);

  const today = getNewDate(TODAY_DATE);
  const minStart = today;
  const maxStart = add(today, { days: ESIM_DATE_LIMITS.MAX_OFFSET_DAYS });
  const minEnd = form.travelStart ? getNewDate(form.travelStart) : minStart;
  const maxEnd = form.travelStart
    ? add(getNewDate(form.travelStart), {
        days: ESIM_DATE_LIMITS.MAX_TRIP_DAYS,
      })
    : maxStart;

  const handleCountryChange = (selected) => {
    setForm((prev) => ({ ...prev, country: selected?.value || '' }));
    setErrors((e) => ({ ...e, country: undefined }));
  };

  const handleStartChange = (date) => {
    const iso = getAPIFormattedDate(date);
    setForm((prev) => {
      // If the previously chosen end is before the new start, clear it.
      const next = { ...prev, travelStart: iso };
      if (prev.travelEnd && new Date(prev.travelEnd) < new Date(iso)) {
        next.travelEnd = '';
      }
      return next;
    });
    setErrors((e) => ({ ...e, travelStart: undefined }));
  };

  const handleEndChange = (date) => {
    const iso = getAPIFormattedDate(date);
    setForm((prev) => ({ ...prev, travelEnd: iso }));
    setErrors((e) => ({ ...e, travelEnd: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.country) next.country = 'Please select a destination country.';
    if (!form.travelStart) next.travelStart = 'Pick your travel start date.';
    if (!form.travelEnd) next.travelEnd = 'Pick your travel end date.';
    if (form.travelStart && form.travelEnd) {
      const days = tripDurationInDays(form.travelStart, form.travelEnd);
      if (days < ESIM_DATE_LIMITS.MIN_TRIP_DAYS) {
        next.travelEnd = 'Trip must be at least 1 day long.';
      } else if (days > ESIM_DATE_LIMITS.MAX_TRIP_DAYS) {
        next.travelEnd = `Trip cannot exceed ${ESIM_DATE_LIMITS.MAX_TRIP_DAYS} days.`;
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    if (onSearch) {
      onSearch(form);
      return;
    }
    const qs = buildEsimSearchQuery(form);
    navigate(`${ESIM_ROUTES.PLANS}?${qs}`);
  };

  const trip = tripDurationInDays(form.travelStart, form.travelEnd);

  return (
    <section
      className={`esim-search-form ${
        compact ? 'esim-search-form--compact' : ''
      }`}
    >
      <div className="esim-search-form__head">
        <h2 className="esim-search-form__title">
          Stay connected anywhere with an eSIM
        </h2>
        <p className="esim-search-form__sub">
          Choose your destination and travel dates — we&apos;ll surface the most
          relevant data plans instantly.
        </p>
      </div>

      <div className="esim-search-form__row">
        <div className="esim-search-form__field esim-search-form__field--country grid-template-full">
          <span className="esim-search-form__label">
            <FiGlobe />
            Destination Country *
          </span>
          <AppSelect
            name="country"
            label=""
            placeholder={
              countriesLoading ? 'Loading countries…' : 'Choose a country'
            }
            selectedValue={form.country}
            options={options}
            onSelect={handleCountryChange}
            isClearable={false}
            maxheight="280px"
          />
          {errors.country && (
            <small className="esim-search-form__err">{errors.country}</small>
          )}
        </div>

        <div className="esim-search-form__field grid-template-half">
          <span className="esim-search-form__label">
            <FiCalendar />
            Travel Start Date *
          </span>
          <AppDatePicker
            name="travelStart"
            label=""
            placeholder=""
            className="app-date-picker"
            minDate={minStart}
            maxDate={maxStart}
            selectedDate={form.travelStart ? getNewDate(form.travelStart) : ''}
            onChange={handleStartChange}
            customClassName={`position-relative flight-booking-picker ${
              errors.travelStart ? 'app-error-text-border' : ''
            }`}
          />
          {errors.travelStart && (
            <small className="esim-search-form__err">
              {errors.travelStart}
            </small>
          )}
        </div>

        <div className="esim-search-form__field grid-template-half-second">
          <span className="esim-search-form__label">
            <FiCalendar />
            Travel End Date *
          </span>
          <AppDatePicker
            name="travelEnd"
            label=""
            placeholder=""
            className="app-date-picker"
            minDate={minEnd}
            maxDate={maxEnd}
            selectedDate={form.travelEnd ? getNewDate(form.travelEnd) : ''}
            onChange={handleEndChange}
            customClassName={`position-relative flight-booking-picker ${
              errors.travelEnd ? 'app-error-text-border' : ''
            }`}
          />
          {errors.travelEnd && (
            <small className="esim-search-form__err">{errors.travelEnd}</small>
          )}
        </div>

        <button
          type="button"
          className="esim-search-form__submit grid-template-full"
          onClick={handleSubmit}
        >
          <span>Search Plans</span>
          <FiArrowRight />
        </button>
      </div>

      {trip > 0 && !errors.travelEnd && (
        <p className="esim-search-form__trip">
          Trip duration: <strong>{trip}</strong> {trip === 1 ? 'day' : 'days'}
        </p>
      )}
    </section>
  );
}

EsimSearchForm.propTypes = {
  initialValues: PropTypes.object,
  onSearch: PropTypes.func,
  compact: PropTypes.bool,
};

EsimSearchForm.defaultProps = {
  initialValues: null,
  onSearch: null,
  compact: false,
};

export default EsimSearchForm;
